import metadata from 'libphonenumber-js/min/metadata';
import React, { useCallback, useEffect, useState } from 'react';
import PhoneNumberInput from 'react-phone-number-input';

import { useGeoCountry } from '../../hooks/use_geo_country';
import PhoneCountrySelect from './phone_country_select';

function detectCountryFromPrefix(input) {
  if (!input) return undefined;

  const clean = input.replace(/\D/g, '');

  for (let len = 3; len >= 1; len--) {
    const prefix = clean.slice(0, len);

    const countries = metadata.country_calling_codes[prefix];
    if (countries) {
      return countries[0];
    }
  }

  return undefined;
}

function sanitizePhoneValue(raw) {
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return undefined;
  return `+${digits}`;
}

export function PhoneInput({
  pushEventTo,
  value,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  target,
  readonly,
  disabled,
  default_country: defaultCountry = 'US',
}) {
  const targetEl = document.getElementById(target);
  const normalizedValue = typeof value === 'string' && value.length > 0 ? value : undefined;
  const normalizedCountry = typeof defaultCountry === 'string' ? defaultCountry.toUpperCase() : undefined;
  const {
    country: geoCountry,
    setCountry: setGeoCountry,
    normalizedDefaultCountry: normalizedGeoDefaultCountry,
    markUserCountryChange,
  } = useGeoCountry({ defaultCountry: normalizedCountry });
  const resolvedDefaultCountry = geoCountry || normalizedGeoDefaultCountry || normalizedCountry;
  const [localValue, setLocalValue] = useState(normalizedValue);
  const [isFocused, setIsFocused] = useState(false);
  const [hasUserInput, setHasUserInput] = useState(() => Boolean(normalizedValue));
  const [forceInternational, setForceInternational] = useState(false);
  const onlyPlus = localValue === '+';
  const detectedCountry = detectCountryFromPrefix(localValue);
  const renderedValue = localValue;
  const countrySelectValue = detectedCountry || (forceInternational || onlyPlus ? undefined : resolvedDefaultCountry);
  const effectiveDefaultCountry = forceInternational || onlyPlus ? undefined : resolvedDefaultCountry;

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(prev => (prev === normalizedValue ? prev : normalizedValue));
    }
    setHasUserInput(prev => prev || Boolean(normalizedValue));
  }, [normalizedValue, isFocused]);

  useEffect(() => {
    if (localValue === '+') {
      setHasUserInput(true);
      setForceInternational(true);
    }
    if (localValue && localValue !== '+') {
      setForceInternational(false);
    }
    if (!localValue) {
      setForceInternational(false);
    }
  }, [localValue]);

  const emit = (eventName, payload = {}) => {
    if (!eventName || !pushEventTo || !targetEl) return;
    pushEventTo(targetEl, eventName, payload);
  };

  const clampPlus = (event, replacement) => {
    const el = event.target;
    const start = el?.selectionStart;
    const end = el?.selectionEnd;
    const value = el?.value || '';
    if (!value.startsWith('+') || start == null || end == null) return;

    const removingPlus =
      start === 0 ||
      (event.key === 'Backspace' && start === end && start <= 1) ||
      (event.key === 'Delete' && start === end && start === 0);
    if (!removingPlus) return;

    event.preventDefault();
    if (!replacement && end <= 1) return;

    setHasUserInput(true);
    const nextValue = `+${replacement || ''}`;
    setLocalValue(nextValue);
    emit(onChange, { value: nextValue });
  };

  const handleKeyDown = event => {
    if (event.key === 'Backspace' || event.key === 'Delete') clampPlus(event);
  };

  const handleBeforeInput = event => {
    if (event.inputType === 'insertFromPaste') return;
    if (!localValue && event.data === '+') {
      event.preventDefault();
      setHasUserInput(true);
      setForceInternational(true);
      setLocalValue('+');
      emit(onChange, { value: '+' });
      return;
    }
    if (event.data) {
      setHasUserInput(true);
      clampPlus(event, event.data);
    }
  };

  const handlePaste = event => {
    const text = event.clipboardData?.getData('text/plain') || event.clipboardData?.getData('text');
    const sanitized = sanitizePhoneValue(text);
    if (!sanitized) return;
    event.preventDefault();

    const pasteDigits = sanitized.replace(/^\+/, '');
    const el = event.target;
    const start = el?.selectionStart;
    const end = el?.selectionEnd;
    const formattedValue = el?.value || '';

    let nextValue = sanitized;

    if (start != null && end != null) {
      const combined = `${formattedValue.slice(0, start)}${pasteDigits}${formattedValue.slice(end)}`;
      const digits = combined.replace(/\D/g, '');
      nextValue = digits ? `+${digits}` : undefined;
    }

    setHasUserInput(prev => prev || Boolean(nextValue));
    setLocalValue(nextValue);
    emit(onChange, { value: nextValue });
  };

  const ensureOnlyPlus = raw => {
    const onlyPlusInput = /^\+\s*$/.test(raw);
    if (onlyPlusInput) {
      setHasUserInput(true);
      setForceInternational(true);
      if (localValue !== '+') {
        setLocalValue('+');
        emit(onChange, { value: '+' });
      }
      return true;
    }
    return false;
  };

  const handleInput = event => {
    const raw = event.target?.value || '';
    ensureOnlyPlus(raw);
  };

  const handleChange = newValue => {
    const incoming = newValue || undefined;
    const hasDigits = typeof incoming === 'string' && /\d/.test(incoming);

    setHasUserInput(prev => prev || Boolean(incoming));
    setLocalValue(incoming);

    if (hasDigits) setForceInternational(false);
    if (!incoming) setForceInternational(false);

    emit(onChange, { value: incoming || null });
  };

  const handleKeyUp = event => {
    const raw = event.target?.value || '';
    ensureOnlyPlus(raw);
  };

  const renderCountrySelect = useCallback(
    props => {
      const { onChange, ...rest } = props;
      return (
        <PhoneCountrySelect
          {...rest}
          value={countrySelectValue}
          onChange={nextCountry => {
            if (nextCountry) {
              markUserCountryChange();
              setGeoCountry(nextCountry.toUpperCase());
            }
            onChange?.(nextCountry);
          }}
        />
      );
    },
    [countrySelectValue, markUserCountryChange, setGeoCountry],
  );

  return (
    <PhoneNumberInput
      aria-label='Phone number'
      countrySelectProps={{ tabIndex: -1 }}
      international
      value={renderedValue}
      disabled={disabled || readonly}
      country={forceInternational || onlyPlus ? undefined : countrySelectValue}
      defaultCountry={forceInternational || onlyPlus ? undefined : effectiveDefaultCountry}
      placeholder={placeholder}
      onBeforeInput={handleBeforeInput}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onInput={handleInput}
      onPaste={handlePaste}
      onFocus={() => {
        setIsFocused(true);
        emit(onFocus, { name: target });
      }}
      onBlur={() => {
        setIsFocused(false);
        emit(onBlur, { name: target });
      }}
      onChange={handleChange}
      countrySelectComponent={renderCountrySelect}
    />
  );
}
